import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { IoMdLock } from "react-icons/io";
import { CiCircleQuestion } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import {
  parsePhoneNumberFromString,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
} from "libphonenumber-js";
import * as flags from "country-flag-icons/react/3x2";
import { HiOutlineMapPin } from "react-icons/hi2";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import toast from "react-hot-toast";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import metadata from "libphonenumber-js/metadata.full.json";
import { REST_API_URL } from "../../api/config";
import { FiSearch, FiMapPin, FiGlobe } from "react-icons/fi";
import { MdSatellite, MdMap, MdMyLocation } from "react-icons/md";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const customIcon = new L.Icon({
  iconUrl:
    "data:image/svg+xml;base64," +
    btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#4F46E5" width="36" height="36">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  `),
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  shadowSize: [41, 41],
  shadowAnchor: [12, 41],
});

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

const buildHeaders = (token, extra = {}) => ({
  "Content-Type": "application/json",
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
  "User-Timezone": getUserTimeZone(),
  ...extra,
});

function MapUpdater({ position }) {
  const map = useMap();

  useEffect(() => {
    if (position) {
      map.setView(position, map.getZoom());
    }
  }, [position, map]);

  return null;
}

function SearchControl({ onSearch, onUseCurrentLocation, searchValue, onSearchChange }) {
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = async () => {
    if (!searchValue.trim()) return;

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          searchValue
        )}&limit=5`
      );

      if (!response.ok) {
        throw new Error("Search failed");
      }

      const results = await response.json();

      if (results.length > 0) {
        const firstResult = results[0];
        const position = {
          lat: parseFloat(firstResult.lat),
          lng: parseFloat(firstResult.lon),
        };

        onSearch(position, firstResult);
      } else {
        toast.error("Location not found. Try a different address.");
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Search failed. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleUseCurrentLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userPosition = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          onUseCurrentLocation(userPosition);
          toast.success("Current location detected!");
        },
        (error) => {
          console.error("Geolocation error:", error);
          toast.error(
            "Unable to get your location. Please allow location access."
          );
        }
      );
    } else {
      toast.error("Geolocation is not supported by your browser.");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div className="relative w-full">
      <div className="relative flex gap-2">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Search for address, city, or landmark..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 focus:ring-opacity-50 transition-all bg-white"
          />
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleSearch}
          disabled={isSearching}
          className="h-10 px-4 bg-indigo-600 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
        >
          {isSearching ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Searching...</span>
            </>
          ) : (
            <>
              <FiSearch className="w-4 h-4" />
              <span>Search</span>
            </>
          )}
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleUseCurrentLocation}
          className="h-10 px-3 bg-gray-100 text-gray-700 rounded-lg font-medium flex items-center gap-2 hover:bg-gray-200 transition-colors"
          title="Use my current location"
        >
          <MdMyLocation className="w-4 h-4" />
        </motion.button>
      </div>
    </div>
  );
}

export default function BiographySalon() {
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [currency, setCurrency] = useState("");
  const [currencies, setCurrencies] = useState([]);
  const [openCurrency, setOpenCurrency] = useState(false);
  const [workOptions, setWorkOptions] = useState({
    studio: false,
    travel: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingCurrency, setLoadingCurrency] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState("US");
  const [countryCode, setCountryCode] = useState("+1");
  const [openCountries, setOpenCountries] = useState(false);
  const [position, setPosition] = useState(null);
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [mapStyle, setMapStyle] = useState("light");
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const userStatus = localStorage.getItem("user_status");

  const mapStyles = {
    light: {
      url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
      icon: <MdMap className="w-4 h-4" />,
    },
    dark: {
      url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
      icon: <FiGlobe className="w-4 h-4" />,
    },
    satellite: {
      url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      icon: <MdSatellite className="w-4 h-4" />,
    },
  };

  const token = useMemo(() => {
    return (
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken") ||
      ""
    ).trim();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenCurrency(false);
        setOpenCountries(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedCountry("US");
    setCountryCode("+1");
  }, []);

  useEffect(() => {
    const geoSuccess = (pos) => {
      const { latitude, longitude } = pos.coords;
      setPosition({ lat: latitude, lng: longitude });
    };

    const geoError = () => setPosition({ lat: 48.8584, lng: 2.2945 });

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(geoSuccess, geoError);
    } else {
      geoError();
    }
  }, []);

  useEffect(() => {
    const fetchAddress = async () => {
      if (!position) return;

      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${position.lat}&lon=${position.lng}&format=json&addressdetails=1`
        );
        const data = await res.json();

        setAddress(data.display_name || "");
        setCity(
          data.address?.city ||
            data.address?.town ||
            data.address?.village ||
            ""
        );
      } catch {
        console.warn("Failed to load address");
      }
    };

    fetchAddress();
  }, [position]);

  const fetchAddressDetails = async (pos) => {
    if (!pos) return;

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${pos.lat}&lon=${pos.lng}&format=json&addressdetails=1`
      );
      const data = await res.json();

      if (data && data.address) {
        const fullAddress = data.display_name || "";
        setAddress(fullAddress);
        setSearchQuery(fullAddress);
        setCity(
          data.address?.city ||
            data.address?.town ||
            data.address?.village ||
            ""
        );
      }
    } catch (error) {
      console.error("Reverse geocoding error:", error);
    }
  };

  const handleSearch = async (newPosition, searchResult) => {
    setPosition(newPosition);

    if (searchResult) {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${newPosition.lat}&lon=${newPosition.lng}&format=json&addressdetails=1`
      );
      const data = await res.json();

      if (data && data.address) {
        const fullAddress = data.display_name || "";
        setAddress(fullAddress);
        setSearchQuery(fullAddress);
        setCity(
          data.address?.city ||
            data.address?.town ||
            data.address?.village ||
            ""
        );
      }
    }

    toast.success("Location found!");
  };

  const handleUseCurrentLocation = (userPosition) => {
    setPosition(userPosition);
    fetchAddressDetails(userPosition);
  };

  useEffect(() => {
    const fetchCurrencies = async () => {
      setLoadingCurrency(true);

      try {
        if (!token) {
          toast.error("Session expired. Please log in again.");
          navigate("/sign-in");
          return;
        }

        const res = await fetch(`${BASE_URL}/service/currencies/`, {
          headers: buildHeaders(token),
        });

        if (res.status === 401) {
          toast.error("Session expired. Please log in again.");
          navigate("/sign-in");
          return;
        }

        if (!res.ok) throw new Error("Failed to load currencies");

        const data = await res.json();

        const formatted = data.map((item) => ({
          code: item.short_name,
          name: item.short_name,
          flag: item.icon || "",
        }));

        setCurrencies(formatted);

        if (formatted.length > 0) {
          setCurrency(formatted[0].code);
        }
      } catch {
        setCurrencies([
          { code: "USD", name: "USD ($)", flag: "https://flagcdn.com/us.svg" },
          { code: "EUR", name: "EUR (€)", flag: "https://flagcdn.com/eu.svg" },
          { code: "RUB", name: "RUB (₽)", flag: "https://flagcdn.com/ru.svg" },
        ]);
        setCurrency("USD");
      } finally {
        setLoadingCurrency(false);
      }
    };

    fetchCurrencies();
  }, [token, navigate]);

  const getNumberLengthRange = (country) => {
    try {
      const example = getExampleNumber(country, metadata);
      if (!example) return null;

      const digits = example.nationalNumber.length;

      return {
        min: digits - 1,
        max: digits + 1,
        example: example.nationalNumber,
      };
    } catch {
      return null;
    }
  };

  const handlePhoneChange = (value) => {
    const cleaned = value.replace(/[^\d+\-\s]/g, "");
    setPhone(cleaned);

    const parsed = parsePhoneNumberFromString(cleaned, selectedCountry);
    const lengthInfo = getNumberLengthRange(selectedCountry);

    if (!parsed) {
      setErrors((prev) => ({
        ...prev,
        phone: "Please enter a valid phone number.",
      }));
      return;
    }

    const numLength = parsed.nationalNumber.length;

    if (lengthInfo) {
      if (numLength < lengthInfo.min) {
        setErrors((prev) => ({
          ...prev,
          phone: `Too short — expected around ${lengthInfo.min}-${lengthInfo.max} digits.`,
        }));
        return;
      }

      if (numLength > lengthInfo.max) {
        setErrors((prev) => ({
          ...prev,
          phone: `Too long — expected around ${lengthInfo.min}-${lengthInfo.max} digits.`,
        }));
        return;
      }
    }

    if (!parsed.isPossible() || !parsed.isValid()) {
      setErrors((prev) => ({
        ...prev,
        phone: "This phone number format is invalid.",
      }));
      return;
    }

    setErrors((prev) => {
      const next = { ...prev };
      delete next.phone;
      return next;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!fullName.trim() || fullName.length < 2) {
      newErrors.fullName = "Full name is required (min 2 characters).";
    }

    if (!companyName.trim() || companyName.length < 2) {
      newErrors.companyName = "Company name is required (min 2 characters).";
    }

    const parsed = parsePhoneNumberFromString(
      phone.replace(/[\s-]/g, ""),
      selectedCountry
    );

    if (!parsed || !parsed.isValid()) {
      newErrors.phone = "Please enter a valid phone number for your country.";
    }

    if (!currency) {
      newErrors.currency = "Please select a currency.";
    }

    if (!position) {
      newErrors.location = "Please select your business location.";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length) {
      toast.error("Please fix the highlighted errors.");
      return false;
    }

    return true;
  };

  const handleNext = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      if (!token) {
        toast.error("Your session has expired. Please sign in again.");
        navigate("/sign-in");
        return;
      }

      const parsed = parsePhoneNumberFromString(phone, selectedCountry);
      const normalizedPhone = parsed?.number || phone;

      const lat = position?.lat || 0;
      const lng = position?.lng || 0;
      const address_url = `https://www.google.com/maps?q=${lat},${lng}`;

      const userTimeZone = getUserTimeZone();

      const postBody = {
        full_name: fullName,
        company_name: companyName,
        phone_number: normalizedPhone,
        city: city || "Unknown",
        address: address || `${lat}, ${lng}`,
        address_url,
        currency,
        work_place_ids: [1],
        worker_emails: [],
        timeZone: userTimeZone,
      };

      const res = await fetch(`${BASE_URL}/auth/userinfo/`, {
        method: "POST",
        headers: buildHeaders(token),
        body: JSON.stringify(postBody),
      });

      const data = await res.json().catch(() => ({}));

      if (res.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate("/sign-in");
        return;
      }

      if (!res.ok) {
        throw new Error(data.detail || "Failed to save biography.");
      }

      localStorage.setItem("salonData", JSON.stringify(postBody));
      localStorage.setItem("user_timezone", userTimeZone);
      localStorage.setItem("user_currency", currency);
      localStorage.setItem("user_country", selectedCountry);

      sessionStorage.setItem("onboarding_force_route", "/biography-member");
      sessionStorage.setItem("onboarding_force_status", "salon");

      toast.success("Salon information saved successfully!");
      navigate("/biography-member", { replace: true });
    } catch (err) {
      console.error("Biography salon error:", err);
      toast.error(err.message || "Failed to save biography.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleEnterKey = (e) => {
      if (
        e.key === "Enter" &&
        !loading &&
        fullName.trim() &&
        companyName.trim() &&
        phone.trim()
      ) {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener("keydown", handleEnterKey);
    return () => window.removeEventListener("keydown", handleEnterKey);
  }, [loading, fullName, companyName, phone]);

  return (
    <motion.div
      className="biography"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <main className="biography-main">
        <motion.div
          className="biography-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="biography-header">
            <div className="biography-text">
              <h2>Biography</h2>
              <p>Tell us about your business</p>
            </div>
          </div>

          <div className="biography-content">
            <div className="biography-form" ref={dropdownRef}>
              <div className="biography-form-group">
                <label>Full name</label>
                <input
                  type="text"
                  placeholder="Type your full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className={errors.fullName ? "invalid" : ""}
                  autoFocus
                />
                {errors.fullName && (
                  <span className="input-error-text">{errors.fullName}</span>
                )}
              </div>

              <div className="biography-form-group">
                <label>Company name *</label>
                <input
                  type="text"
                  placeholder="Type your company name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className={errors.companyName ? "invalid" : ""}
                />
                {errors.companyName && (
                  <span className="input-error-text">{errors.companyName}</span>
                )}
              </div>

              <div className="biography-form-group">
                <label>Phone Number</label>
                <div className="phone-field">
                  <div
                    className="phone-prefix"
                    onClick={() => {
                      setOpenCountries((prev) => !prev);
                      setOpenCurrency(false);
                    }}
                  >
                    {React.createElement(flags[selectedCountry] || flags.US, {
                      title: selectedCountry,
                      style: { width: 24, height: 18, marginRight: 6 },
                    })}
                    <span className="country-code">{countryCode}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M7 10l5 5 5-5"
                        stroke="#9A99AB"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  <input
                    type="tel"
                    inputMode="tel"
                    pattern="[0-9+\-\s]*"
                    value={phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    className={`phone-input ${errors.phone ? "invalid" : ""}`}
                  />

                  {openCountries && (
                    <div className="country-dropdown">
                      {getCountries().map((c) => (
                        <div
                          key={c}
                          className="country-item"
                          onClick={() => {
                            setSelectedCountry(c);
                            setCountryCode("+" + getCountryCallingCode(c));
                            setOpenCountries(false);
                          }}
                        >
                          {React.createElement(flags[c] || flags.US, {
                            title: c,
                            style: { width: 20, height: 14, marginRight: 8 },
                          })}
                          <span className="country-name">{c}</span>
                          <span className="country-dial">
                            +{getCountryCallingCode(c)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {errors.phone && (
                  <span className="input-error-text">{errors.phone}</span>
                )}
              </div>

              <div className="biography-form-group biography-form-group-currency">
                <label>Currency</label>

                <div
                  className={`currency-field ${errors.currency ? "invalid" : ""}`}
                  onClick={() => {
                    if (!loadingCurrency) {
                      setOpenCurrency((prev) => !prev);
                      setOpenCountries(false);
                    }
                  }}
                >
                  <span className="currency-display">
                    {loadingCurrency ? (
                      "Loading..."
                    ) : (
                      <>
                        {currencies.find((c) => c.code === currency)?.flag && (
                          <img
                            src={currencies.find((c) => c.code === currency)?.flag}
                            alt={currency}
                            className="currency-flag"
                            style={{
                              width: 20,
                              height: 14,
                              marginRight: 6,
                              objectFit: "contain",
                            }}
                          />
                        )}
                        {currency || "Select currency"}
                      </>
                    )}
                  </span>

                  <svg
                    className={`currency-down ${openCurrency ? "open" : ""}`}
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M7 10l5 5 5-5"
                      stroke="#9A99AB"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                {openCurrency && currencies.length > 0 && (
                  <div className="currency-menu">
                    {currencies.map((item) => (
                      <div
                        key={item.code}
                        className="currency-item"
                        onClick={() => {
                          setCurrency(item.code);
                          setOpenCurrency(false);
                        }}
                      >
                        {item.flag && (
                          <img
                            src={item.flag}
                            alt={item.name}
                            className="currency-flag"
                          />
                        )}
                        <span className="currency-name">{item.name}</span>
                        <div
                          className={`currency-radio ${
                            currency === item.code ? "selected" : ""
                          }`}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {errors.currency && (
                  <span className="input-error-text">{errors.currency}</span>
                )}
              </div>
            </div>

            <div className="biography-map">
              <label>City / Address *</label>

              <div className="mb-4">
                <SearchControl
                  onSearch={handleSearch}
                  onUseCurrentLocation={handleUseCurrentLocation}
                  searchValue={searchQuery}
                  onSearchChange={setSearchQuery}
                />
              </div>

              {position ? (
                <motion.div
                  initial={{ opacity: 1, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="relative rounded-2xl overflow-hidden border-2 border-gray-200 shadow-2xl group"
                >
                  <div className="absolute top-4 left-4 z-10 flex gap-2">
                    <div className="flex gap-1 bg-white/95 backdrop-blur-sm rounded-xl p-1 border border-gray-200 shadow-sm">
                      {Object.keys(mapStyles).map((style) => (
                        <motion.button
                          key={style}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setMapStyle(style)}
                          className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-xs font-medium transition-all ${
                            mapStyle === style
                              ? "bg-indigo-600 text-white"
                              : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                          }`}
                        >
                          {mapStyles[style].icon}
                          <span className="capitalize">{style}</span>
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  <MapContainer
                    center={position}
                    zoom={14}
                    style={{
                      height: "350px",
                      width: "100%",
                    }}
                    className="rounded-2xl"
                    scrollWheelZoom={true}
                    attributionControl={false}
                  >
                    <TileLayer url={mapStyles[mapStyle].url} attribution="" />
                    <MapUpdater position={position} />
                    <Marker
                      position={position}
                      icon={customIcon}
                      draggable
                      eventHandlers={{
                        dragstart: () => {
                          document.body.style.cursor = "grabbing";
                        },
                        dragend: async (e) => {
                          document.body.style.cursor = "";
                          const newPosition = e.target.getLatLng();
                          setPosition(newPosition);
                          await fetchAddressDetails(newPosition);
                        },
                      }}
                    />
                  </MapContainer>

                  <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-3 rounded-xl shadow-xl border border-gray-200">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                        <FiMapPin className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          Drag marker to adjust location
                        </p>
                        <p className="text-xs text-gray-500">
                          Or click anywhere on map
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="absolute top-4 right-4 bg-black/80 text-white px-3 py-2 rounded-lg text-xs font-mono backdrop-blur-sm">
                    {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
                  </div>
                </motion.div>
              ) : (
                <p className="check-location">
                  <HiOutlineMapPin />
                </p>
              )}

              {errors.location && (
                <span className="input-error-text">{errors.location}</span>
              )}
            </div>
          </div>

          {userStatus !== "salon" && (
            <div className="biography-work">
              <label className="biography-work-title">Where do you work?</label>

              <div
                className={`biography-checkbox-group ${errors.work ? "invalid" : ""}`}
              >
                <label>
                  <input
                    type="checkbox"
                    checked={workOptions.studio}
                    onChange={() =>
                      setWorkOptions((prev) => ({
                        ...prev,
                        studio: !prev.studio,
                      }))
                    }
                  />
                  <span>
                    I work at my own place or studio, whether I own or rent it,
                    and/or with other professionals
                  </span>
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={workOptions.travel}
                    onChange={() =>
                      setWorkOptions((prev) => ({
                        ...prev,
                        travel: !prev.travel,
                      }))
                    }
                  />
                  <span>I travel to my clients' location to provide services.</span>
                </label>
              </div>

              {errors.work && (
                <span className="input-error-text">{errors.work}</span>
              )}
            </div>
          )}

          <div className="biography-btn-footer">
            <motion.button
              className="biography-next-btn"
              onClick={handleNext}
              disabled={loading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {loading ? "Saving..." : "Next"}
            </motion.button>

            <span className="biography-step">Step 1/5</span>
          </div>
        </motion.div>
      </main>
    </motion.div>
  );
}